/**
 * Anonymous gameplay telemetry.
 *
 * This deliberately records a play session, not a person or a device. The session id is
 * random, lives only in memory, and disappears when the tab closes. No cookie, storage,
 * account, IP address or user-agent value is read here.
 *
 * Telemetry is also non-authoritative: every network failure is ignored. Losing a count
 * must never alter, pause or slow the deterministic game underneath it.
 */

import type { QualityTier } from '../render/world';
import type { ShiftProgress } from '../sim/types';

export const TELEMETRY_HEARTBEAT_MS = 120_000;

export type TelemetryEventName =
  | 'shift_start' | 'heartbeat' | 'shift_end' | 'shift_leave' | 'boot_stall' | 'visit';
export type TelemetryMode = 'campaign' | 'endless';
export type TelemetryOutcome = 'running' | 'complete' | 'defeat' | 'abandon' | 'closed';

export interface TelemetryEvent {
  schema: 3;
  session: string;
  event: TelemetryEventName;
  mode: TelemetryMode;
  shift: number;
  outcome: TelemetryOutcome;
  quality: QualityTier;
  elapsed: number;
  arrivalsScheduled: number;
  arrivalsSafe: number;
  arrivalsLost: number;
  hostilesDestroyed: number;
  roundsFired: number;
  roundsHit: number;
  oilDelivered: number;
  earned: number;
  source: string;
  medium: string;
  campaign: string;
}

export interface TelemetryAttribution {
  /** UTM source when present; otherwise the referring hostname or `direct`. */
  source: string;
  /** Optional UTM medium. */
  medium: string;
  /** Optional UTM campaign. */
  campaign: string;
}

export type TelemetryTransport = (event: TelemetryEvent, terminal: boolean) => void;

interface ActiveShift {
  mode: TelemetryMode;
  shift: number;
  quality: QualityTier;
}

export interface GameTelemetryOptions {
  enabled: boolean;
  session: string;
  send: TelemetryTransport;
  attribution?: TelemetryAttribution;
  now?: () => number;
}

const DIRECT_ATTRIBUTION: Readonly<TelemetryAttribution> = {
  source: 'direct',
  medium: '',
  campaign: '',
};

/**
 * Turn campaign values into small, public-safe labels rather than storing arbitrary URL
 * text. This deliberately drops paths, search terms and punctuation that could carry
 * personal information. The server independently enforces the same alphabet and limits.
 */
function attributionToken(value: string | null, maxLength: number): string {
  if (!value) return '';
  return value
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._~-]/g, '')
    .replace(/^[._~-]+|[._~-]+$/g, '')
    .slice(0, maxLength);
}

/**
 * Read one page load's source without cookies or persistent identifiers.
 *
 * UTM labels win because they describe a link we intentionally published. Without one,
 * only the referring HOSTNAME is retained — never its path or query string. Same-site
 * referrers and browsers that withhold a referrer become `direct`.
 */
export function telemetryAttribution(url: string, referrer: string): TelemetryAttribution {
  let page: URL;
  try {
    page = new URL(url);
  } catch {
    return { ...DIRECT_ATTRIBUTION };
  }

  const source = attributionToken(page.searchParams.get('utm_source'), 96);
  const medium = attributionToken(page.searchParams.get('utm_medium'), 64);
  const campaign = attributionToken(page.searchParams.get('utm_campaign'), 96);
  if (source) return { source, medium, campaign };

  try {
    const referringHost = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
    const ownHost = page.hostname.toLowerCase().replace(/^www\./, '');
    const referringSource = attributionToken(referringHost, 96);
    if (referringSource && referringSource !== ownHost) {
      return { source: referringSource, medium, campaign };
    }
  } catch {
    // Empty, withheld and malformed referrers are all honestly "direct / unknown".
  }

  return { source: 'direct', medium, campaign };
}

/** Convert the campaign's internal Endless indices (101, 102...) to watch numbers. */
export function telemetryPosition(index: number): Pick<ActiveShift, 'mode' | 'shift'> {
  return index >= 100
    ? { mode: 'endless', shift: Math.max(1, index - 100) }
    : { mode: 'campaign', shift: Math.max(1, index) };
}

function finite(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/**
 * Owns the small state machine that turns a running shift into sparse aggregate events.
 *
 * Two-minute heartbeats keep the free allowance generous while making "playing now" a
 * useful three-minute estimate. A start and an end are separate events so abandonment
 * and difficulty can be measured without trying to infer them from page views.
 */
export class GameTelemetry {
  private readonly now: () => number;
  private active: ActiveShift | null = null;
  private lastHeartbeatAt = 0;

  constructor(private readonly options: GameTelemetryOptions) {
    this.now = options.now ?? (() => performance.now());
  }

  startShift(index: number, quality: QualityTier, shift: Readonly<ShiftProgress>): void {
    if (!this.options.enabled) return;

    const position = telemetryPosition(index);
    this.active = { ...position, quality };
    this.lastHeartbeatAt = this.now();
    this.emit('shift_start', 'running', shift, false);
  }

  tick(shift: Readonly<ShiftProgress>): void {
    if (!this.active) return;

    const now = this.now();
    if (now - this.lastHeartbeatAt < TELEMETRY_HEARTBEAT_MS) return;
    this.lastHeartbeatAt = now;
    this.emit('heartbeat', 'running', shift, false);
  }

  endShift(
    outcome: Extract<TelemetryOutcome, 'complete' | 'defeat'>,
    shift: Readonly<ShiftProgress>,
  ): void {
    if (!this.active) return;
    this.emit('shift_end', outcome, shift, true);
    this.active = null;
  }

  abandonShift(shift: Readonly<ShiftProgress>): void {
    if (!this.active) return;
    this.emit('shift_end', 'abandon', shift, true);
    this.active = null;
  }

  /**
   * Best-effort final signal for a closed or navigated-away tab.
   *
   * The active shift is intentionally retained: a page stored in the back-forward cache
   * can return without being reconstructed, and its heartbeat should then resume.
   */
  leaveShift(shift: Readonly<ShiftProgress>): void {
    if (!this.active) return;
    this.emit('shift_leave', 'closed', shift, true);
  }

  private emit(
    event: TelemetryEventName,
    outcome: TelemetryOutcome,
    shift: Readonly<ShiftProgress>,
    terminal: boolean,
  ): void {
    if (!this.active) return;

    this.options.send({
      schema: 3,
      session: this.options.session,
      event,
      mode: this.active.mode,
      shift: this.active.shift,
      outcome,
      quality: this.active.quality,
      elapsed: finite(shift.elapsed),
      arrivalsScheduled: finite(shift.arrivalsScheduled),
      arrivalsSafe: finite(shift.arrivalsSafe),
      arrivalsLost: finite(shift.arrivalsLost),
      hostilesDestroyed: finite(shift.hostilesDestroyed),
      roundsFired: finite(shift.roundsFired),
      roundsHit: finite(shift.roundsHit),
      oilDelivered: finite(shift.oilDelivered),
      earned: finite(shift.earned),
      ...(this.options.attribution ?? DIRECT_ATTRIBUTION),
    }, terminal);
  }
}

function sessionId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

let cachedPageSession: string | null = null;
let cachedPageAttribution: TelemetryAttribution | null = null;

function pageSession(): string {
  cachedPageSession ??= sessionId();
  return cachedPageSession;
}

function pageAttribution(): TelemetryAttribution {
  cachedPageAttribution ??= telemetryAttribution(window.location.href, document.referrer);
  return cachedPageAttribution;
}

function browserTransport(event: TelemetryEvent, terminal: boolean): void {
  const body = JSON.stringify(event);

  if (terminal && typeof navigator.sendBeacon === 'function') {
    try {
      const queued = navigator.sendBeacon(
        '/api/telemetry',
        new Blob([body], { type: 'application/json' }),
      );
      if (queued) return;
    } catch {
      // Fall through to the equally best-effort fetch path.
    }
  }

  void fetch('/api/telemetry', {
    method: 'POST',
    body,
    headers: { 'content-type': 'application/json' },
    cache: 'no-store',
    credentials: 'omit',
    keepalive: terminal,
  }).catch(() => {
    // Telemetry is allowed to disappear. Gameplay is not allowed to notice.
  });
}

/**
 * One-shot report that a boot reached the title gate.
 *
 * Exists because the page-view layer (Cloudflare's beacon script) is exactly the kind of
 * third-party request ad blockers eat, and the launch-day audience blocks aggressively —
 * so "how many people actually opened the game" was uncountable. This is first-party,
 * fires once per load, and carries the tier, boot time and privacy-safe source labels.
 * The later `shift_start` uses the same memory-only session key, which is what makes the
 * source funnel possible without identifying or remembering a person.
 */
export function sendVisit(quality: QualityTier, bootSeconds: number): void {
  if (window.location.hostname !== 'deepwater.stratara.ai') return;
  browserTransport({
    schema: 3,
    session: pageSession(),
    event: 'visit',
    mode: 'campaign',
    shift: 0,
    outcome: 'running',
    quality,
    elapsed: finite(bootSeconds),
    arrivalsScheduled: 0,
    arrivalsSafe: 0,
    arrivalsLost: 0,
    hostilesDestroyed: 0,
    roundsFired: 0,
    roundsHit: 0,
    oilDelivered: 0,
    earned: 0,
    ...pageAttribution(),
  }, false);
}

/**
 * One-shot report that a boot stalled and recovery kicked in (see bootRecovery.ts).
 *
 * Deliberately as anonymous as the gameplay events: no renderer string, no user agent,
 * no error text — just which tier failed, which attempt this was and how long the player
 * waited, so the incidence of the launch-day "almost ready" hang stays measurable.
 * Terminal transport: the page is usually about to reload itself.
 */
export function sendBootStall(tried: QualityTier, attempt: number, elapsedSeconds: number): void {
  if (window.location.hostname !== 'deepwater.stratara.ai') return;
  browserTransport({
    schema: 3,
    session: pageSession(),
    event: 'boot_stall',
    mode: 'campaign',
    shift: finite(attempt),
    outcome: 'closed',
    quality: tried,
    elapsed: finite(elapsedSeconds),
    arrivalsScheduled: 0,
    arrivalsSafe: 0,
    arrivalsLost: 0,
    hostilesDestroyed: 0,
    roundsFired: 0,
    roundsHit: 0,
    oilDelivered: 0,
    earned: 0,
    ...pageAttribution(),
  }, true);
}

/**
 * Production-only factory.
 *
 * Local development, benchmark captures, automated tests, Pages previews and the
 * pages.dev fallback do not enter the real player dataset.
 */
export function createGameTelemetry(): GameTelemetry {
  return new GameTelemetry({
    enabled: window.location.hostname === 'deepwater.stratara.ai',
    session: pageSession(),
    send: browserTransport,
    attribution: pageAttribution(),
  });
}
